/**
 * Backend Call Graph Page (Issues #864 Phase B, #867)
 *
 * Reuses all Call Graph explorer views (explorer, list, matrix, tree,
 * subgraph) by converting the BCG data into CallGraphData shape.
 * Adds caller-count overlays via the stitched-mappings endpoint.
 *
 * Data source: GET /projects/:id/backend-call-graph
 */

import { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import type { CallGraphData, CallGraphNode, CallGraphEdge, FunctionNodeType } from '@veodiagram/shared';
import { api } from '../services/api';
import {
  FunctionExplorer,
  FunctionList,
  DependencyMatrix,
  FocusedSubgraph,
  TreeView,
} from '../components/callGraph';

type ViewMode = 'explorer' | 'list' | 'matrix' | 'subgraph' | 'tree';

// ─── Convert BCG → CallGraphData ───

interface BCGEndpoint {
  routeId: string;
  routeKey: { framework: string; method: string; path: string; handlerSymbol: string; filePath: string };
  method: string;
  path: string;
  framework: string;
  filePath: string;
  lineStart: number;
  lineEnd: number;
  handlerFunction: string;
}

interface BCGData {
  nodes: Record<string, unknown>;
  edges: Array<{ id?: string; from?: string; to?: string; source?: string; target?: string; label?: string }>;
  endpoints: BCGEndpoint[];
  stats: { endpoints: number; functions: number; dbLeaves: number; externalLeaves: number; unknownFrameworkRoutes: number };
}

function shortenPath(p: string): string {
  const parts = p.split('/');
  return parts.length <= 3 ? p : '.../' + parts.slice(-3).join('/');
}

// veodiagram-issues#45.B — extract host from an absolute URL pattern so
// the BCG renderer can show it as the secondary label. Mirrors the
// helper in E2EFlowNodes (#45.A); kept local to avoid an indirect
// import path from a page module.
function extractHost(url: string | undefined | null): string | null {
  if (!url) return null;
  try {
    const withScheme = url.startsWith('//') ? `http:${url}` : url;
    return new URL(withScheme).host || null;
  } catch {
    const m = url.match(/^(?:https?:)?\/\/([^/?#]+)/i);
    return m?.[1] ?? null;
  }
}

function convertBCGToCallGraphData(
  bcg: BCGData,
  callerMap: Record<string, number>,
): CallGraphData {
  const nodes: CallGraphNode[] = [];
  const edges: CallGraphEdge[] = [];
  const entryPoints: string[] = [];
  const leafNodes: string[] = [];
  const hotspots: Array<{ id: string; callerCount: number }> = [];
  const nodeIds = new Set<string>();

  for (const ep of bcg.endpoints) {
    const callers = callerMap[ep.routeId] ?? 0;
    const desc = callers > 0 ? `${callers} caller${callers !== 1 ? 's' : ''} via stitches` : 'no stitched callers';
    nodes.push({
      id: ep.routeId,
      name: `${ep.method.toUpperCase()} ${ep.path}`,
      qualifiedName: `${ep.method.toUpperCase()} ${ep.path}`,
      module: ep.framework || 'unknown',
      type: 'entry' as FunctionNodeType,
      isAsync: false,
      isEntryPoint: true,
      isExternal: false,
      description: `${ep.framework} · ${ep.handlerFunction} · ${desc}`,
      calledCount: callers,
      callCount: 0,
      location: ep.filePath ? { file: ep.filePath, startLine: ep.lineStart, endLine: ep.lineEnd } : undefined,
    });
    entryPoints.push(ep.routeId);
    nodeIds.add(ep.routeId);
    if (callers >= 3) hotspots.push({ id: ep.routeId, callerCount: callers });
  }

  for (const [id, raw] of Object.entries(bcg.nodes)) {
    if (nodeIds.has(id)) continue;
    const node = raw as Record<string, unknown>;
    const name = (node.name as string) || (node.label as string) || id;
    const kind = (node.kind as string) || (node.type as string) || 'function';
    const filePath = (node.filePath as string) || '';
    // Fix #870: use actual BCG node kinds from backendCallGraphBuilder.ts
    // R3#4 — verified against backendCallGraphBuilder.ts: emitter only
    // produces 'DbCall'; the prior fallback to 'dbQuery'/'database'
    // was dead code from an older shape, pruned to keep the consumer
    // honest with the emitter contract.
    let type: FunctionNodeType = 'function';
    const isDb = kind === 'DbCall';
    const isExtCall = kind === 'ExternalCall';
    if (isDb || isExtCall) type = 'external';
    else if (kind === 'middleware') type = 'method';

    // veodiagram-issues#45.B — propagate BCG kind + ExternalCall
    // metadata so the renderer can visually distinguish DbCall (db)
    // from ExternalCall (network), and within ExternalCall the
    // category=='http' nodes (which actually leave the system) from
    // category=='sdk' (which stay in-process).
    //
    // R1#8 / R1#9 — narrow each BCG field with a runtime typeof guard.
    // The BCG API responses have caused real bugs in the past (see #869
    // `from`/`to` vs `source`/`target`); a malformed value here would
    // otherwise reach the renderer and trip `new URL(<non-string>)`.
    let extData: Record<string, unknown> | undefined;
    if (isDb) {
      extData = { bcgKind: 'DbCall' };
    } else if (isExtCall) {
      const urlPattern = typeof node.urlPattern === 'string' ? node.urlPattern : undefined;
      const service = typeof node.service === 'string' ? node.service : undefined;
      const category =
        node.category === 'http' || node.category === 'sdk' ? node.category : undefined;
      extData = {
        bcgKind: 'ExternalCall',
        category,
        service,
        urlPattern,
        host: extractHost(urlPattern) ?? undefined,
      };
    }

    nodes.push({
      id, name, qualifiedName: name,
      module: filePath ? shortenPath(filePath) : 'unknown',
      type, isAsync: false, isEntryPoint: false, isExternal: type === 'external',
      calledCount: 0, callCount: 0,
      location: filePath ? { file: filePath, startLine: (node.lineStart as number) || 0, endLine: (node.lineEnd as number) || 0 } : undefined,
      ...(extData ? { data: extData } : {}),
    });
    nodeIds.add(id);
  }

  // Fix #869: BCG edges use `from`/`to`, not `source`/`target`
  for (let i = 0; i < bcg.edges.length; i++) {
    const e = bcg.edges[i] as { id?: string; from?: string; to?: string; source?: string; target?: string; label?: string };
    const src = e.from ?? e.source ?? '';
    const tgt = e.to ?? e.target ?? '';
    if (!nodeIds.has(src) || !nodeIds.has(tgt)) continue;
    edges.push({
      id: e.id || `bcg-edge-${i}`, source: src, target: tgt,
      isAsync: false, isMethodCall: false, callCount: 1, callSites: [], confidence: 1, label: e.label,
    });
  }

  const hasOutgoing = new Set(edges.map(e => e.source));
  for (const n of nodes) { if (!hasOutgoing.has(n.id)) leafNodes.push(n.id); }
  for (const e of edges) {
    const t = nodes.find(n => n.id === e.target);
    const s = nodes.find(n => n.id === e.source);
    if (t) t.calledCount = (t.calledCount || 0) + 1;
    if (s) s.callCount = (s.callCount || 0) + 1;
  }
  hotspots.sort((a, b) => b.callerCount - a.callerCount);

  return {
    metadata: { title: 'Backend Call Graph', diagramType: 'callGraph' as const, c4Level: 'code' as const },
    nodes, edges, entryPoints, leafFunctions: leafNodes, criticalPaths: [],
    hotspots: hotspots.slice(0, 20), unusedFunctions: [],
    stats: {
      totalFunctions: nodes.length, internalFunctions: nodes.filter(n => !n.isExternal).length,
      externalCalls: nodes.filter(n => n.isExternal).length,
      avgCallsPerFunction: nodes.length > 0 ? edges.length / nodes.length : 0, maxCallDepth: 0,
    },
  };
}

// ─── Main Component ───

export function BackendGraphPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const [callGraphData, setCallGraphData] = useState<CallGraphData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('explorer');
  const [selectedFunctionId, setSelectedFunctionId] = useState<string | null>(null);
  const [subgraphRootId, setSubgraphRootId] = useState<string | null>(null);
  const [subgraphHistory, setSubgraphHistory] = useState<string[]>([]);
  const [moduleFilter, setModuleFilter] = useState('');
  const [typeFilters, setTypeFilters] = useState<Set<FunctionNodeType>>(new Set());
  const [endpointCount, setEndpointCount] = useState(0);

  const modules = useMemo(() => {
    if (!callGraphData) return [];
    const s = new Set<string>();
    for (const n of callGraphData.nodes) s.add(n.module);
    return Array.from(s).sort();
  }, [callGraphData]);

  const filteredCallGraphData = useMemo((): CallGraphData | null => {
    if (!callGraphData) return null;
    if (!moduleFilter && typeFilters.size === 0) return callGraphData;
    const filteredNodes = callGraphData.nodes.filter(n => {
      if (moduleFilter && n.module !== moduleFilter) return false;
      if (typeFilters.size > 0 && !typeFilters.has(n.type)) return false;
      return true;
    });
    const nodeIds = new Set(filteredNodes.map(n => n.id));
    const filteredEdges = callGraphData.edges.filter(e => nodeIds.has(e.source) && nodeIds.has(e.target));
    return { ...callGraphData, nodes: filteredNodes, edges: filteredEdges };
  }, [callGraphData, moduleFilter, typeFilters]);

  const fetchData = useCallback(async () => {
    if (!projectId) return;
    setIsLoading(true); setError(null);
    try {
      const [bcgRes, mappings] = await Promise.all([
        api.getBackendCallGraph(projectId),
        api.getStitchedMappings(projectId).catch(() => ({ callerToEndpoint: {}, endpointToCallers: {} })),
      ]);
      const bcg = bcgRes.backendCallGraph as unknown as BCGData;
      if (!bcg || !bcg.endpoints) { setError('No backend call graph found. Run analysis to generate one.'); return; }
      const cm: Record<string, number> = {};
      for (const [routeId, callers] of Object.entries(mappings.endpointToCallers ?? {})) cm[routeId] = (callers as unknown[]).length;
      setEndpointCount(bcg.endpoints.length);
      const cgData = convertBCGToCallGraphData(bcg, cm);
      setCallGraphData(cgData);
      if (cgData.entryPoints.length > 0) setSelectedFunctionId(cgData.entryPoints[0]);
      else if (cgData.nodes.length > 0) setSelectedFunctionId(cgData.nodes[0].id);
    } catch (err) { setError(err instanceof Error ? err.message : 'Failed to load backend call graph'); }
    finally { setIsLoading(false); }
  }, [projectId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSelectFunction = useCallback((id: string) => setSelectedFunctionId(id), []);
  const handleViewInExplorer = useCallback((id: string) => { setSelectedFunctionId(id); setViewMode('explorer'); }, []);
  const handleFocusFunction = useCallback((id: string) => { setSubgraphRootId(id); setSubgraphHistory([]); setViewMode('subgraph'); }, []);
  const handleSelectEdge = useCallback((sourceId: string) => { setSelectedFunctionId(sourceId); setViewMode('explorer'); }, []);
  const handleSubgraphBack = useCallback(() => {
    if (subgraphHistory.length > 0) { setSubgraphRootId(subgraphHistory[subgraphHistory.length - 1]); setSubgraphHistory(h => h.slice(0, -1)); }
  }, [subgraphHistory]);
  const handleSubgraphNavigateToIndex = useCallback((i: number) => {
    const target = subgraphHistory[i];
    if (target) { setSubgraphHistory(prev => prev.slice(0, i)); setSubgraphRootId(target); }
  }, [subgraphHistory]);
  const handleCloseSubgraph = useCallback(() => { setViewMode('explorer'); setSubgraphRootId(null); setSubgraphHistory([]); }, []);

  if (isLoading) return (
    <div className="min-h-screen bg-slate-50 dark:bg-gray-900 flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600" />
        <p className="text-slate-600 dark:text-slate-400">Loading backend call graph...</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-slate-50 dark:bg-gray-900 flex items-center justify-center">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 max-w-md text-center">
        <h2 className="text-xl font-semibold mb-2">Error</h2>
        <p className="text-slate-600 dark:text-slate-400 mb-4">{error}</p>
        <div className="flex gap-4 justify-center">
          <button onClick={fetchData} className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700">Retry</button>
          <Link to={`/projects/${projectId}`} className="px-4 py-2 bg-gray-200 dark:bg-gray-700 rounded">Back</Link>
        </div>
      </div>
    </div>
  );

  if (!callGraphData) return null;

  return (
    <div className="h-screen bg-slate-50 dark:bg-gray-900 flex flex-col overflow-hidden" data-testid="backend-graph-page">
      <header className="bg-white dark:bg-gray-800 border-b border-slate-200 dark:border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to={`/projects/${projectId}`} className="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200">← Back to Project</Link>
            <h1 className="text-xl font-semibold text-slate-800 dark:text-white" data-testid="page-title">Backend Call Graph</h1>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
              {(['explorer', 'list', 'matrix', 'tree', 'subgraph'] as ViewMode[]).map(v => (
                <button key={v} onClick={() => { if (v === 'subgraph') setSubgraphRootId(selectedFunctionId); setViewMode(v); }}
                  className={`px-3 py-1.5 text-sm rounded-md transition-colors capitalize ${viewMode === v ? 'bg-white dark:bg-gray-600 shadow text-orange-600 dark:text-orange-400 font-medium' : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'}`}
                >{v}</button>
              ))}
            </div>
            <button onClick={fetchData} className="px-3 py-1.5 bg-orange-600 text-white text-sm rounded hover:bg-orange-700">Refresh</button>
          </div>
        </div>
      </header>

      <div className="bg-white dark:bg-gray-800 border-b border-slate-200 dark:border-gray-700 px-6 py-2 flex items-center gap-4 text-sm flex-wrap" data-testid="stats-bar">
        <span><strong>{endpointCount}</strong> endpoints</span>
        <span><strong>{filteredCallGraphData?.nodes.length ?? 0}</strong> total nodes</span>
        <span><strong>{filteredCallGraphData?.edges.length ?? 0}</strong> edges</span>
        <div className="flex-1" />
        <div className={`flex items-center gap-2 flex-wrap transition-opacity ${(viewMode === 'explorer' || viewMode === 'subgraph') ? 'opacity-40 pointer-events-none' : ''}`}>
          <select value={moduleFilter} onChange={e => setModuleFilter(e.target.value)} className="px-2 py-1 border border-slate-300 dark:border-gray-600 rounded text-sm dark:bg-gray-700">
            <option value="">All frameworks</option>
            {modules.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          {(moduleFilter || typeFilters.size > 0) && (
            <button onClick={() => { setModuleFilter(''); setTypeFilters(new Set()); }} className="px-2 py-0.5 text-xs text-orange-600 hover:underline">Clear</button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-hidden">
        {viewMode === 'explorer' && filteredCallGraphData && <FunctionExplorer data={filteredCallGraphData} selectedFunctionId={selectedFunctionId || undefined} onSelectFunction={handleSelectFunction} onFocusFunction={handleFocusFunction} />}
        {viewMode === 'list' && filteredCallGraphData && <FunctionList data={filteredCallGraphData} selectedFunctionId={selectedFunctionId || undefined} onSelectFunction={handleSelectFunction} onViewInExplorer={handleViewInExplorer} onViewSubgraph={handleFocusFunction} />}
        {viewMode === 'matrix' && filteredCallGraphData && <DependencyMatrix data={filteredCallGraphData} onSelectFunction={handleSelectFunction} onSelectEdge={handleSelectEdge} />}
        {viewMode === 'tree' && filteredCallGraphData && <TreeView data={filteredCallGraphData} selectedFunctionId={selectedFunctionId || undefined} onSelectFunction={handleSelectFunction} onViewInExplorer={handleViewInExplorer} onViewSubgraph={handleFocusFunction} />}
        {viewMode === 'subgraph' && subgraphRootId && filteredCallGraphData && (
          <FocusedSubgraph data={filteredCallGraphData} rootFunctionId={subgraphRootId}
            onSelectFunction={(id) => { setSubgraphHistory(h => [...h, subgraphRootId!]); setSubgraphRootId(id); }}
            onBack={handleSubgraphBack} onClose={handleCloseSubgraph}
            historyFunctionIds={subgraphHistory} onNavigateToHistoryIndex={handleSubgraphNavigateToIndex} />
        )}
      </div>
    </div>
  );
}
